import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { clearStoredAccessToken, getStoredAccessToken, setStoredAccessToken } from '@/lib/auth'
import type { AuthApiData } from '@/types/auth'
import { authApi } from '@/store/api/authApi'

interface AuthState {
	token: string | null
	lifeCycle: number | null
	email: string | null
	usernameRequired: boolean
}

const initialState: AuthState = {
	token: getStoredAccessToken(),
	lifeCycle: null,
	email: null,
	usernameRequired: false,
}

function applyAuthData(state: AuthState, data: AuthApiData | undefined) {
	if (!data?.token) {
		return
	}

	state.token = data.token
	state.lifeCycle = data.life_cycle
	state.email = data.email ?? null
	if (typeof data.has_username === 'boolean') {
		state.usernameRequired = !data.has_username
	}
	setStoredAccessToken(data.token)
}

const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		setCredentials(state, action: PayloadAction<AuthApiData>) {
			applyAuthData(state, action.payload)
		},
		setUsernameRequired(state, action: PayloadAction<boolean>) {
			state.usernameRequired = action.payload
		},
		logout(state) {
			state.token = null
			state.lifeCycle = null
			state.email = null
			state.usernameRequired = false
			clearStoredAccessToken()
		},
	},
	extraReducers: (builder) => {
		builder.addMatcher(authApi.endpoints.login.matchFulfilled, (state, action) => {
			applyAuthData(state, action.payload.data)
		})
		builder.addMatcher(authApi.endpoints.googleLogin.matchFulfilled, (state, action) => {
			applyAuthData(state, action.payload.data)
		})
		builder.addMatcher(authApi.endpoints.metaMaskLogin.matchFulfilled, (state, action) => {
			applyAuthData(state, action.payload.data)
		})
		builder.addMatcher(authApi.endpoints.setUsername.matchFulfilled, (state) => {
			state.usernameRequired = false
		})
	},
})

export const { setCredentials, setUsernameRequired, logout } = authSlice.actions

export const selectAccessToken = (state: { auth: AuthState }) => state.auth.token

export const selectIsAuthenticated = (state: { auth: AuthState }) => Boolean(getStoredAccessToken())
export const selectUsernameRequired = (state: { auth: AuthState }) => state.auth.usernameRequired

export default authSlice.reducer
